/**
 * PrintedPage: the page of a code book as the reviewer reads it (PRD-625), drawn once for every screen that
 * shows it -- the walk, the rule and the held tray. The image, the marks laid on it (a leaf, a fence run, a
 * span, the box being dragged, the settled box), the page arrows, the cited-page return, the zoom, and a
 * drag that reports the rectangle it drew in points. Pure presentation: it takes an image address (or the
 * words of a refusal), boxes and callbacks, and never calls the server (docs/STRATA.md); the reads that feed
 * it live in the api layer. The mark kinds are variants, matched exhaustively, never a value's class.
 */
import { useEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { PAGE, RADIUS, TEXT, WALK, WASH_ALPHA, WEIGHT, Z, tint, usePalette, type Hex, type Palette } from '../theme';
import { ActionButton } from './action-button';
import { Notice } from './notice';
import {
  boxOfDrag,
  cropOf,
  fractionsOfPixels,
  fractionsOfPoints,
  pixelToPoint,
  type Fractions,
  type PageFrame,
  type PagePoint,
  type PixelBox,
  type PointBox,
} from './page-frame';
import { Stack } from './panel';

/** The picture: ready to draw, or why it cannot be (the refusal's own words, never a blank image). */
export type PageImage =
  | { readonly kind: 'ready'; readonly src: string }
  | { readonly kind: 'refused'; readonly words: string };

/** The sheet beside this one: a way to go there, or the book's edge. */
export type PageStep = { readonly kind: 'linked'; readonly go: () => void } | { readonly kind: 'edge' };

/** The way back to the page a citation named: nothing to return to, or a way there. */
export type CitedReturn = { readonly kind: 'here' } | { readonly kind: 'away'; readonly go: () => void };

/** How the print is shown: fitted to its column, or enlarged by a factor of that fit. */
export type PageZoom = { readonly kind: 'fit' } | { readonly kind: 'zoomed'; readonly factor: number };

/** Whether a drag draws on the page: a drag draws and reports the rectangle in points, or the page is still. */
export type DrawMode = { readonly kind: 'drag'; readonly onDraw: (box: PointBox) => void } | { readonly kind: 'still' };

/** What a leaf's box says of the leaf (Figma 00): ruled (approved), the one being read (reviewing), a draft, or a box the reviewer drew. */
export type LeafStatus = 'approved' | 'reviewing' | 'draft' | 'new';

/** A leaf on the page, in points, with its status and the label its box wears ("kind · headword · status"). */
export interface LeafMark {
  readonly kind: 'leaf';
  readonly box: PointBox;
  readonly status: LeafStatus;
  readonly label: string;
}

/** The popover that follows a drawn box: nothing, or a card under the box (the words, the kind, OK). */
export type BoxPopover = { readonly kind: 'none' } | { readonly kind: 'under'; readonly box: PointBox; readonly children: ReactNode };

/** The bar of page arrows, fit and zoom above the print: shown, or left off where the frame shows none. */
export type PageControls = { readonly kind: 'shown' } | { readonly kind: 'hidden' };

/** A run of numbered lines a fence names, in the pixels of the numbered image. */
export interface FenceMark {
  readonly kind: 'fence';
  readonly frame: PageFrame;
  readonly run: PixelBox;
}

/** A span of the reader's words lit on the page, in points. */
export interface SpanMark {
  readonly kind: 'span';
  readonly box: PointBox;
}

/** Where the flex put a dragged box, shown before OK: dashed, because nothing is written yet. */
export interface SettledBox {
  readonly kind: 'settled';
  readonly box: PointBox;
}

/** The rectangle the reviewer dragged, kept on the page after the release (Figma 4, "as drawn"): dashed grey, its words below. */
export interface AsDrawnBox {
  readonly kind: 'as-drawn';
  readonly box: PointBox;
  readonly label: string;
}

/** The box the flex made of the drag (Figma 4, "settled"): solid blue on a faint wash, its words above. Shown, never written. */
export interface FlexedBox {
  readonly kind: 'flexed';
  readonly box: PointBox;
  readonly label: string;
}

/** The box being dragged right now. */
export interface DrawnBox {
  readonly kind: 'drawn';
  readonly box: PointBox;
}

/** The marks a screen hands the page. */
export type PageMark = LeafMark | FenceMark | SpanMark | SettledBox | AsDrawnBox | FlexedBox;

/** Every mark the page draws, the dragged box included. */
type DrawnMark = PageMark | DrawnBox;

/** The paint a mark wears: a leaf by its status, a fence run, a span and the dragged box each their own. */
function markPaint(p: Palette, mark: DrawnMark): Hex {
  switch (mark.kind) {
    case 'leaf':
      return leafPaint(p, mark.status);
    case 'fence':
      return p.absence;
    case 'span':
      return p.categorical[4];
    case 'settled':
    case 'as-drawn':
      return p.canvasProvisional;
    case 'flexed':
      return p.blue;
    case 'drawn':
      return p.ink;
  }
}

/** The paint of a leaf's box by its status: green ruled, amber being read, blue draft or new. */
function leafPaint(p: Palette, status: LeafStatus): Hex {
  switch (status) {
    case 'approved':
      return p.categorical[2];
    case 'reviewing':
      return p.absence;
    case 'draft':
    case 'new':
      return p.blue;
  }
}

/** The pale ground and the deeper ink a leaf's label wears by its status. */
function labelPaints(p: Palette, status: LeafStatus): { readonly ground: Hex; readonly ink: Hex } {
  switch (status) {
    case 'approved':
      return { ground: p.approvedWash, ink: p.approvedInk };
    case 'reviewing':
      return { ground: p.absenceWash, ink: p.absenceInk };
    case 'draft':
    case 'new':
      return { ground: p.blueWash, ink: p.blueInk };
  }
}

/** A mark's place as fractions of the drawn page. */
function markPlace(mark: DrawnMark, extent: PointBox): Fractions {
  switch (mark.kind) {
    case 'leaf':
    case 'span':
    case 'settled':
    case 'as-drawn':
    case 'flexed':
    case 'drawn':
      return fractionsOfPoints(mark.box, extent);
    case 'fence':
      return fractionsOfPixels(mark.run, mark.frame);
  }
}

/** The line a mark's border draws: dashed for the settled box, solid for every other. */
function borderLine(mark: DrawnMark): 'dashed' | 'solid' {
  switch (mark.kind) {
    case 'settled':
    case 'as-drawn':
      return 'dashed';
    case 'flexed':
    case 'leaf':
    case 'fence':
    case 'span':
    case 'drawn':
      return 'solid';
  }
}

function percent(fraction: number): string {
  return `${fraction * 100}%`;
}

/** The wash inside a mark: a leaf's box is barely tinted so the print reads through; the settled box has none. */
function markWash(mark: DrawnMark): number {
  switch (mark.kind) {
    case 'leaf':
      return mark.status === 'reviewing' ? PAGE.leafWashBeing : PAGE.leafWash;
    case 'settled':
    case 'as-drawn':
      return 0;
    case 'flexed':
      return PAGE.leafWash;
    case 'fence':
    case 'span':
    case 'drawn':
      return PAGE.markWash;
  }
}

/** The label above a leaf's box and, on the leaf being read, the four handles at its corners. */
function LeafDress({ mark }: { mark: LeafMark }) {
  const p = usePalette();
  const { ground, ink } = labelPaints(p, mark.status);
  const paint = leafPaint(p, mark.status);
  const corner = -(PAGE.markBorder + WALK.handle / 2);
  const handle = {
    position: 'absolute',
    width: WALK.handle,
    height: WALK.handle,
    boxSizing: 'border-box',
    background: p.surface,
    border: `${PAGE.markBorder}px solid ${paint}`,
  } satisfies CSSProperties;
  return (
    <>
      <span
        style={{
          position: 'absolute',
          left: -PAGE.markBorder,
          bottom: `calc(100% + ${PAGE.markBorder + WALK.labelGap}px)`,
          height: WALK.labelHeight,
          display: 'inline-flex',
          alignItems: 'center',
          padding: `${WALK.labelPadY}px ${WALK.labelPadX}px`,
          boxSizing: 'border-box',
          borderRadius: RADIUS.xs,
          background: ground,
          color: ink,
          fontSize: TEXT.nano,
          fontWeight: WEIGHT.strong,
          lineHeight: 'normal',
          whiteSpace: 'nowrap',
        }}
      >
        {mark.label}
      </span>
      {mark.status === 'reviewing' && (
        <>
          <span data-handle="" style={{ ...handle, left: corner, top: corner }} />
          <span data-handle="" style={{ ...handle, right: corner, top: corner }} />
          <span data-handle="" style={{ ...handle, left: corner, bottom: corner }} />
          <span data-handle="" style={{ ...handle, right: corner, bottom: corner }} />
        </>
      )}
    </>
  );
}

/** The words a held box wears: "settled: ..." above the flexed box on a bordered chip, "as drawn" below the dragged one. */
function NoteDress({ mark }: { mark: AsDrawnBox | FlexedBox }) {
  const p = usePalette();
  const flexed = mark.kind === 'flexed';
  return (
    <span
      style={{
        position: 'absolute',
        left: -PAGE.markBorder,
        ...(flexed ? { bottom: `calc(100% + ${PAGE.markBorder + WALK.labelGap}px)` } : { top: `calc(100% + ${PAGE.markBorder}px)` }),
        padding: `${WALK.labelPadY}px ${WALK.noteLabelPadX}px`,
        borderRadius: RADIUS.sm,
        background: p.surface,
        border: flexed ? `1px solid ${tint(p.blue, WASH_ALPHA.flexedEdge)}` : '1px solid transparent',
        color: flexed ? p.blueInk : p.inkMuted,
        fontSize: TEXT.micro,
        fontWeight: flexed ? WEIGHT.strong : WEIGHT.regular,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
      }}
    >
      {mark.label}
    </span>
  );
}

function MarkView({ mark, extent }: { mark: DrawnMark; extent: PointBox }) {
  const p = usePalette();
  const paint = markPaint(p, mark);
  const place = markPlace(mark, extent);
  const inset = mark.kind === 'settled';
  const style = {
    position: 'absolute',
    left: percent(place.left),
    top: percent(place.top),
    width: inset ? `calc(${percent(place.width)} - ${PAGE.settledInsetX * 2}px)` : percent(place.width),
    height: inset ? `calc(${percent(place.height)} - ${PAGE.settledInsetY * 2}px)` : percent(place.height),
    marginLeft: inset ? PAGE.settledInsetX : undefined,
    marginTop: inset ? PAGE.settledInsetY : undefined,
    boxSizing: 'border-box',
    border: `${PAGE.markBorder}px ${borderLine(mark)} ${paint}`,
    background: tint(paint, markWash(mark)),
    borderRadius: mark.kind === 'settled' ? 0 : mark.kind === 'as-drawn' || mark.kind === 'flexed' ? RADIUS.xs : RADIUS.sm,
    pointerEvents: 'none',
    zIndex: Z.overlay,
  } satisfies CSSProperties;
  return (
    <div data-mark={mark.kind} style={style}>
      {mark.kind === 'leaf' && <LeafDress mark={mark} />}
      {(mark.kind === 'as-drawn' || mark.kind === 'flexed') && <NoteDress mark={mark} />}
    </div>
  );
}

function Arrow({ step, label, glyph }: { step: PageStep; label: string; glyph: string }) {
  switch (step.kind) {
    case 'linked':
      return (
        <ActionButton size="sm" aria-label={label} onPress={step.go}>
          {glyph}
        </ActionButton>
      );
    case 'edge':
      return (
        <ActionButton size="sm" aria-label={label} isDisabled>
          {glyph}
        </ActionButton>
      );
  }
}

function zoomWidth(zoom: PageZoom): string {
  switch (zoom.kind) {
    case 'fit':
      return '100%';
    case 'zoomed':
      return percent(zoom.factor);
  }
}

function zoomedIn(zoom: PageZoom): PageZoom {
  switch (zoom.kind) {
    case 'fit':
      return { kind: 'zoomed', factor: PAGE.zoomStep };
    case 'zoomed':
      return { kind: 'zoomed', factor: zoom.factor * PAGE.zoomStep };
  }
}

function CitedLink({ cited }: { cited: CitedReturn }) {
  switch (cited.kind) {
    case 'here':
      return null;
    case 'away':
      return (
        <ActionButton size="sm" onPress={cited.go}>
          Back to the cited page
        </ActionButton>
      );
  }
}

export function PrintedPage({
  image,
  extent,
  frame,
  marks,
  previous,
  next,
  cited,
  zoom,
  onZoom,
  draw,
  controls,
  popover,
}: {
  image: PageImage;
  /** The page box in points: what a drag's pixels convert against. */
  extent: PointBox;
  /** The image's own pixel size, which fixes its shape before it loads. */
  frame: PageFrame;
  marks: readonly PageMark[];
  previous: PageStep;
  next: PageStep;
  cited: CitedReturn;
  zoom: PageZoom;
  onZoom: (zoom: PageZoom) => void;
  /** Whether a drag draws on the page: the rectangle it reports, or the page is still and takes no drag. */
  draw: DrawMode;
  /** The arrows, Fit and Zoom above the print, or none (the kind walk's frame shows the sheet alone). */
  controls: PageControls;
  /** The card that follows a drawn box, under it, or none. */
  popover: BoxPopover;
}) {
  const ground = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ from: PagePoint; to: PagePoint } | null>(null);
  /** The drag as the handlers last saw it: read at release, so the parent hears of it from the pointer's own
   * event and never from inside a state updater (which React runs while rendering). */
  const heldRef = useRef<{ from: PagePoint; to: PagePoint } | null>(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;

  function hold(next: { from: PagePoint; to: PagePoint } | null) {
    heldRef.current = next;
    setDrag(next);
  }

  /** The page point under a mouse event, by the box the image is drawn in right now. */
  function pointOf(event: { clientX: number; clientY: number }): PagePoint | null {
    const rect = ground.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const drawn = { width: rect.width, height: rect.height };
    return pixelToPoint({ px: event.clientX - rect.left, py: event.clientY - rect.top }, drawn, extent);
  }

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const move = (event: MouseEvent) => {
      const point = pointOf(event);
      const held = heldRef.current;
      if (point && held) hold({ from: held.from, to: point });
    };
    const up = (event: MouseEvent) => {
      const point = pointOf(event);
      const held = heldRef.current;
      const mode = drawRef.current;
      hold(null);
      if (held && point && mode.kind === 'drag') mode.onDraw(boxOfDrag(held.from, point));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  });

  function press(event: ReactMouseEvent) {
    if (draw.kind === 'still') return;
    const point = pointOf(event);
    if (point) hold({ from: point, to: point });
  }

  const all: readonly DrawnMark[] = drag ? [...marks, { kind: 'drawn', box: boxOfDrag(drag.from, drag.to) }] : marks;

  return (
    <Stack gap="sm">
      {controls.kind === 'shown' && (
        <Stack direction="row" gap="sm" align="center" wrap>
          <Arrow step={previous} label="Previous page" glyph="←" />
          <Arrow step={next} label="Next page" glyph="→" />
          <ActionButton size="sm" onPress={() => onZoom({ kind: 'fit' })}>
            Fit
          </ActionButton>
          <ActionButton size="sm" onPress={() => onZoom(zoomedIn(zoom))}>
            Zoom in
          </ActionButton>
          <CitedLink cited={cited} />
        </Stack>
      )}
      <PageGround image={image} frame={frame} zoom={zoom} ground={ground} onPress={press} spill={popover.kind === 'under'}>
        {all.map((mark, index) => (
          <MarkView key={`${mark.kind}-${index}`} mark={mark} extent={extent} />
        ))}
        <PopoverView popover={popover} extent={extent} />
      </PageGround>
    </Stack>
  );
}

/** The card under a drawn box: where the walk's frame sets it, a gap below the box and at the sheet's text margin. */
function PopoverView({ popover, extent }: { popover: BoxPopover; extent: PointBox }) {
  switch (popover.kind) {
    case 'none':
      return null;
    case 'under': {
      const place = fractionsOfPoints(popover.box, extent);
      // Under the box, as the frame sets it; above it when the box sits low and the card would run off the sheet.
      const above = place.top + place.height > WALK.popoverFlip;
      return (
        <div
          role="dialog"
          aria-label="New box"
          onMouseDown={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            left: percent(WALK.popoverLeft),
            ...(above
              ? { bottom: `calc(${percent(1 - place.top)} + ${WALK.popoverGap + WALK.labelHeight}px)` }
              : { top: `calc(${percent(place.top + place.height)} + ${WALK.popoverGap}px)` }),
            width: WALK.popover,
            zIndex: Z.popover,
            cursor: 'default',
          }}
        >
          {popover.children}
        </div>
      );
    }
  }
}

/** The ground the image and its marks share: scrolls when zoomed, shows a refusal as its words. */
function PageGround({
  image,
  frame,
  zoom,
  ground,
  onPress,
  spill,
  children,
}: {
  image: PageImage;
  frame: PageFrame;
  zoom: PageZoom;
  ground: React.RefObject<HTMLDivElement | null>;
  onPress: (event: ReactMouseEvent) => void;
  /** A card stands over the page and may reach past its edge: the ground does not scroll or clip it. */
  spill: boolean;
  children: React.ReactNode;
}) {
  switch (image.kind) {
    case 'refused':
      return (
        <Notice kind="error" title="The page cannot be shown">
          {image.words}
        </Notice>
      );
    case 'ready':
      return (
        <div style={{ overflow: spill ? 'visible' : 'auto', maxHeight: '100%' }}>
          <div
            ref={ground}
            onMouseDown={onPress}
            style={{
              position: 'relative',
              width: zoomWidth(zoom),
              aspectRatio: `${frame.width} / ${frame.height}`,
              cursor: 'crosshair',
              userSelect: 'none',
            }}
          >
            <img
              src={image.src}
              alt="The printed page"
              draggable={false}
              style={{ display: 'block', width: '100%', height: '100%', borderRadius: RADIUS.sm }}
            />
            {children}
          </div>
        </div>
      );
  }
}

/** The settled box enlarged: the page's own print at the box's scale, the box filling the window beside the page. */
export function CroppedPrint({ src, box, extent, label }: { src: string; box: PointBox; extent: PointBox; label: string }) {
  const crop = cropOf(box, extent);
  return (
    <div
      role="img"
      aria-label={label}
      style={{ position: 'relative', overflow: 'hidden', width: '100%', aspectRatio: crop.aspect }}
    >
      <img alt="" src={src} style={{ position: 'absolute', width: crop.width, left: crop.left, top: crop.top }} />
    </div>
  );
}
